/**
 * AppointmentFormDialog - Dialog pentru creare/editare programare
 * Design compact consistent cu restul aplicației
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete,
  Box,
  Chip,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';
import { format } from 'date-fns';

import {
  AppointmentCreate,
  AppointmentPriority,
  TimeSlot,
  APPOINTMENT_PRIORITY_CONFIG,
} from '../../types/appointments';
import { createAppointment, getDeviceAvailability } from '../../services/appointmentsApi';
import { getDevices } from '../../services/devicesApi';
import { getInvestigationTypes } from '../../services/investigationTypesApi';
import { DeviceListItem } from '../../types/devices';
import { InvestigationTypeListItem } from '../../types/investigationTypes';
import apiClient from '../../services/api';

// Compact styles
const compactInputSx = {
  '& .MuiInputBase-root': { fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
};

const dialogTitleSx = {
  py: 1.5,
  px: 2,
  fontSize: '1rem',
  fontWeight: 600,
  borderBottom: '1px solid',
  borderColor: 'divider'
};

const dialogActionsSx = {
  px: 2,
  py: 1.5,
  borderTop: '1px solid',
  borderColor: 'divider'
};

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  cnp: string | null;
  phone: string | null;
}

interface AppointmentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AppointmentFormDialog: React.FC<AppointmentFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  // Form state
  const [formData, setFormData] = useState<Partial<AppointmentCreate>>({
    priority: 'normal',
    duration_minutes: 30,
  });
  
  // Options
  const [patients, setPatients] = useState<Patient[]>([]);
  const [devices, setDevices] = useState<DeviceListItem[]>([]);
  const [investigationTypes, setInvestigationTypes] = useState<InvestigationTypeListItem[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  
  // Selected
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceListItem | null>(null);
  const [selectedInvestigation, setSelectedInvestigation] = useState<InvestigationTypeListItem | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  
  // Error
  const [error, setError] = useState<string | null>(null);

  // Load devices and investigation types on open
  useEffect(() => {
    if (open) {
      loadDevices();
      loadInvestigationTypes();
      // Reset form
      setFormData({ priority: 'normal', duration_minutes: 30 });
      setSelectedPatient(null);
      setSelectedDevice(null);
      setSelectedInvestigation(null);
      setSelectedDate(null);
      setSelectedSlot(null);
      setAvailableSlots([]);
      setError(null);
    }
  }, [open]);

  const loadDevices = async () => {
    try {
      const response = await getDevices({ status: 'active', limit: 100 });
      setDevices(response.items);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const loadInvestigationTypes = async () => {
    try {
      const response = await getInvestigationTypes({ isActive: true, limit: 100 });
      setInvestigationTypes(response.items);
    } catch (err) {
      console.error('Error loading investigation types:', err);
    }
  };

  // Search patients
  const searchPatients = useCallback(async (search: string) => {
    if (search.length < 2) {
      setPatients([]);
      return;
    }
    
    setPatientsLoading(true);
    try {
      const response = await apiClient.get(`/patients?search=${encodeURIComponent(search)}&size=20`);
      setPatients(response.data?.patients || response.data?.items || []);
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (patientSearch) {
        searchPatients(patientSearch);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [patientSearch, searchPatients]);

  // Load available slots when device and date change
  useEffect(() => {
    if (selectedDevice && selectedDate) {
      loadAvailableSlots();
    } else {
      setAvailableSlots([]);
      setSelectedSlot(null);
    }
  }, [selectedDevice, selectedDate, formData.duration_minutes]);

  const loadAvailableSlots = async () => {
    if (!selectedDevice || !selectedDate) return;
    
    setSlotsLoading(true);
    try {
      const response = await getDeviceAvailability(
        selectedDevice.id,
        format(selectedDate, 'yyyy-MM-dd'),
        formData.duration_minutes || 30
      );
      setAvailableSlots(response.available_slots);
      setSelectedSlot(null);
    } catch (err) {
      console.error('Error loading slots:', err);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  // Update duration when investigation changes
  useEffect(() => {
    if (selectedInvestigation) {
      setFormData(prev => ({
        ...prev,
        investigation_type_id: selectedInvestigation.id,
        duration_minutes: selectedInvestigation.default_duration_minutes,
      }));
    }
  }, [selectedInvestigation]);

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedPatient || !selectedDevice || !selectedInvestigation || !selectedDate || !selectedSlot) {
      setError('Completați toate câmpurile obligatorii');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await createAppointment({
        patient_id: selectedPatient.id,
        device_id: selectedDevice.id,
        investigation_type_id: selectedInvestigation.id,
        scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
        scheduled_start_time: selectedSlot.start_time,
        duration_minutes: formData.duration_minutes || 30,
        priority: formData.priority || 'normal',
        reason: formData.reason,
        notes: formData.notes,
        patient_notes: formData.patient_notes,
      });
      
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Eroare la salvare';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Available slots that are free
  const freeSlots = availableSlots.filter(s => s.is_available);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={dialogTitleSx}>
          Programare Nouă
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            {/* Pacient */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: 'primary.main' }}>
                1. Selectați pacientul
              </Typography>
              <Autocomplete
                options={patients}
                getOptionLabel={(p) => `${p.last_name} ${p.first_name}${p.cnp ? ` (${p.cnp})` : ''}`}
                value={selectedPatient}
                onChange={(_, v) => {
                  setSelectedPatient(v);
                  if (v) setFormData(prev => ({ ...prev, patient_id: v.id }));
                }}
                inputValue={patientSearch}
                onInputChange={(_, v) => setPatientSearch(v)}
                loading={patientsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pacient *"
                    placeholder="Căutați după nume sau CNP..."
                    size="small"
                    sx={compactInputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {patientsLoading && <CircularProgress size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={patientSearch.length < 2 ? 'Introduceți min. 2 caractere' : 'Niciun pacient găsit'}
              />
              {selectedPatient && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    <strong>{selectedPatient.last_name} {selectedPatient.first_name}</strong>
                    {selectedPatient.cnp && ` | CNP: ${selectedPatient.cnp}`}
                    {selectedPatient.phone && ` | Tel: ${selectedPatient.phone}`}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Investigație */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: 'primary.main' }}>
                2. Selectați investigația
              </Typography>
              <Autocomplete
                options={investigationTypes}
                getOptionLabel={(i) => `${i.code} - ${i.name}`}
                value={selectedInvestigation}
                onChange={(_, v) => setSelectedInvestigation(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tip investigație *"
                    size="small"
                    sx={compactInputSx}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                        {option.code} - {option.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        Durată: {option.default_duration_minutes} min
                        {option.preparation_required && ' | Necesită pregătire'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
              {selectedInvestigation?.preparation_required && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem', py: 0.5 }}>
                  Această investigație necesită pregătire prealabilă
                </Alert>
              )}
            </Grid>

            {/* Aparat */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: 'primary.main' }}>
                3. Selectați aparatul
              </Typography>
              <Autocomplete
                options={devices}
                getOptionLabel={(d) => `${d.code} - ${d.name} (${d.room})`}
                value={selectedDevice}
                onChange={(_, v) => setSelectedDevice(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Aparat *"
                    size="small"
                    sx={compactInputSx}
                  />
                )}
              />
            </Grid>

            {/* Data și Slot */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: 'primary.main' }}>
                4. Selectați data și ora
              </Typography>
              <DatePicker
                label="Data *"
                value={selectedDate}
                onChange={(v) => setSelectedDate(v)}
                minDate={new Date()}
                slotProps={{
                  textField: { size: 'small', fullWidth: true, sx: compactInputSx }
                }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, opacity: 0 }}>
                .
              </Typography>
              {slotsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography sx={{ fontSize: '0.8rem' }}>Se încarcă sloturile...</Typography>
                </Box>
              ) : selectedDevice && selectedDate ? (
                freeSlots.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {freeSlots.map((slot, idx) => (
                      <Chip
                        key={idx}
                        label={slot.start_time.slice(0, 5)}
                        size="small"
                        color={selectedSlot === slot ? 'primary' : 'default'}
                        variant={selectedSlot === slot ? 'filled' : 'outlined'}
                        onClick={() => setSelectedSlot(slot)}
                        sx={{ 
                          fontSize: '0.75rem', 
                          height: 28,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>
                    Nu există sloturi disponibile pentru această zi
                  </Typography>
                )
              ) : (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  Selectați aparatul și data pentru a vedea sloturile disponibile
                </Typography>
              )}
            </Grid>

            {/* Prioritate */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={compactInputSx}>
                <InputLabel>Prioritate</InputLabel>
                <Select
                  value={formData.priority || 'normal'}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    priority: e.target.value as AppointmentPriority 
                  }))}
                  label="Prioritate"
                >
                  {Object.entries(APPOINTMENT_PRIORITY_CONFIG).map(([key, cfg]) => (
                    <MenuItem key={key} value={key} sx={{ fontSize: '0.8rem' }}>
                      {cfg.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Durată (min)"
                type="number"
                value={formData.duration_minutes || 30}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  duration_minutes: parseInt(e.target.value) || 30 
                }))}
                inputProps={{ min: 5, max: 480 }}
                sx={compactInputSx}
              />
            </Grid>

            {/* Note */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Motiv / Indicație"
                multiline
                rows={2}
                value={formData.reason || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                sx={compactInputSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Note pentru pacient"
                multiline
                rows={2}
                value={formData.patient_notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, patient_notes: e.target.value }))}
                placeholder="Instrucțiuni pregătire, ce să aducă..."
                sx={compactInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={dialogActionsSx}>
          <Button onClick={onClose} size="small" sx={{ fontSize: '0.8rem' }}>
            Anulează
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !selectedPatient || !selectedDevice || !selectedInvestigation || !selectedDate || !selectedSlot}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {loading ? <CircularProgress size={16} /> : 'Creează Programare'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AppointmentFormDialog;
